import { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../config/firebase';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updatePassword,
  updateEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { ref, get, set, update } from 'firebase/database';
import { isPlatformSuspended } from '../utils/accessSuspensions';

const AuthContext = createContext();

const canSkipEmailVerification = (role) => role === 'student' || role === 'admin';

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign in with email and password
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );

      // Get additional user data from database
      const userRef = ref(db, `users/${userCredential.user.uid}`);
      const snapshot = await get(userRef);
      const userData = snapshot.val();

      // Check if user exists and is active
      if (
        !userData ||
        userData.status === 'inactive' ||
        isPlatformSuspended(userData)
      ) {
        await signOut(auth);
        throw new Error(
          userData && isPlatformSuspended(userData)
            ? 'Your account access has been suspended. Please contact support.'
            : 'Your account has been deactivated. Please contact support.',
        );
      }

      if (!canSkipEmailVerification(userData.role) && !userCredential.user.emailVerified) {
        await signOut(auth);
        throw new Error('Please verify your email before logging in.');
      }

      if (userCredential.user.emailVerified && !userData.emailVerified) {
        await update(userRef, {
          emailVerified: true,
        });
      }

      return {
        ...userData,
        ...userCredential.user,
        emailVerified:
          userCredential.user.emailVerified ||
          canSkipEmailVerification(userData.role),
      };
    } catch (error) {
      if (
        error.code === 'auth/invalid-credential' ||
        error.code === 'auth/user-not-found' ||
        error.code === 'auth/wrong-password'
      ) {
        throw error;
      }
      throw error;
    }
  };

  // Register a new user
  const register = async (email, password, userData) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );

      // Send email verification
      await sendEmailVerification(userCredential.user);

      // Save additional user data to database
      const userRef = ref(db, `users/${userCredential.user.uid}`);
      await set(userRef, {
        ...userData,
        email,
        emailVerified: false,
        createdAt: new Date().toISOString(),
      });

      return userCredential.user;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => signOut(auth);
  const resetPassword = (email) => sendPasswordResetEmail(auth, email);
  const verifyEmail = () => sendEmailVerification(auth.currentUser);
  const updateUserEmail = (newEmail) => updateEmail(auth.currentUser, newEmail);
  const updateUserPassword = (newPassword) =>
    updatePassword(auth.currentUser, newPassword);

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);

      // Check if user exists in database
      const userRef = ref(db, `users/${userCredential.user.uid}`);
      const snapshot = await get(userRef);

      if (!snapshot.exists()) {
        // Create new user in database
        await set(userRef, {
          firstName: userCredential.user.displayName?.split(' ')[0] || '',
          lastName:
            userCredential.user.displayName?.split(' ').slice(1).join(' ') ||
            '',
          email: userCredential.user.email,
          photoURL: userCredential.user.photoURL,
          role: 'student',
          status: 'active',
          emailVerified: true,
          createdAt: new Date().toISOString(),
        });
      }

      return userCredential.user;
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userRef = ref(db, `users/${user.uid}`);
          const snapshot = await get(userRef);
          const userData = snapshot.val();
          if (userData && isPlatformSuspended(userData)) {
            await signOut(auth);
            setCurrentUser(null);
            return;
          }

          if (user.emailVerified && userData && !userData.emailVerified) {
            await update(userRef, {
              emailVerified: true,
            });
          }

          setCurrentUser({
            ...userData,
            ...user,
            emailVerified:
              user.emailVerified || canSkipEmailVerification(userData?.role),
          });
        } catch (error) {
          console.error('Error fetching user data:', error);
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading,
    login,
    logout,
    register,
    resetPassword,
    verifyEmail,
    updateUserEmail,
    updateUserPassword,
    signInWithGoogle,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
